import { useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferMetaDataList from 'hooks/Offers/useOfferMetaDataList'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { getQueryParams } from 'lib/search/searchUtils'
import type { PredictionData, ActiveBooking } from '../types/wrapped'
import type { WrappedStoryDispatchAction } from 'contexts/Wrapped'
import { WrappedStoryActions } from 'contexts/Wrapped'

interface UseWrappedPredictionDataOptions {
  predictionDestinationName: string | undefined
  activeBooking: ActiveBooking | undefined
  dispatch: React.Dispatch<WrappedStoryDispatchAction>
}

interface UseWrappedPredictionDataResult {
  /** True while typeahead search is in progress */
  isTypeaheadLoading: boolean
  /** True while offer list is being fetched */
  isOfferListLoading: boolean
  /** True while any prediction data is loading */
  isLoading: boolean
}

function useWrappedPredictionData({
  predictionDestinationName,
  activeBooking,
  dispatch,
}: UseWrappedPredictionDataOptions): UseWrappedPredictionDataResult {
  const enabled = !!predictionDestinationName && !activeBooking

  // Fetch typeahead data for the prediction destination
  const typeaheadSearchKey = useMemo(() => uuidv4(), [])
  const { streamingData: typeaheadResult } = useTypeaheadStreaming(
    predictionDestinationName ?? '',
    typeaheadSearchKey,
    { enabled, debounceMs: 0, limit: 5 },
  )

  // Find a matching result with an image
  const predictionTypeaheadResult = useMemo(() => {
    if (typeaheadResult?.fetching) return undefined
    return typeaheadResult?.results?.data?.find(result => !!result.display.imageId)
  }, [typeaheadResult])

  const predictionPlaceId = useMemo(() => {
    const extractedData = predictionTypeaheadResult?.searchItem.extractedData
    if (extractedData?.searchType === 'destination' || extractedData?.searchType === 'landmark') {
      return extractedData.placeIds?.[0]
    }
    return predictionTypeaheadResult?.value
  }, [predictionTypeaheadResult])

  // Pre-fetch offer data for the prediction destination
  const predictionFilters: App.OfferListFilters = useMemo(() => ({
    query: predictionDestinationName,
    offerTypes: ['hotel', 'tactical_ao_hotel', 'bundle_and_save', 'bedbank_hotel', 'rental'],
    include: ['nearby'],
    isStream: true,
    destinationId: predictionPlaceId,
    sortBy: 'value',
    limit: 1,
  }), [predictionPlaceId, predictionDestinationName])

  const predictionOfferList = useOfferList(predictionFilters, {
    disabled: !predictionTypeaheadResult || !!activeBooking,
  })
  const predictionMeta = useOfferMetaDataList(predictionFilters)

  // Build the prediction data object once all data is ready
  useEffect(() => {
    if (!predictionTypeaheadResult || activeBooking) return

    const lowestPrice = predictionMeta?.[0]?.pricing.lowestPrice ?? 0
    const lowestPriceValue = predictionMeta?.[0]?.pricing.lowestPriceValue ?? 0
    const savingsPercent = getValueOffPercent(lowestPriceValue, lowestPrice)

    const destinationName = predictionTypeaheadResult.display.mainText
    const searchUrl = `/search?${getQueryParams('', predictionPlaceId ?? '', destinationName)}`

    const predictionData: PredictionData = {
      destinationId: predictionPlaceId ?? '',
      destinationName,
      offerCount: predictionOfferList.offerCount ?? 0,
      maxSavingsPercent: savingsPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? savingsPercent : 0,
      imageId: predictionTypeaheadResult.display.imageId ?? '',
      searchUrl,
    }

    dispatch({
      type: WrappedStoryActions.setPredictionData,
      predictionData,
    })
  }, [predictionTypeaheadResult, predictionMeta, predictionOfferList.offerCount, activeBooking, dispatch, predictionPlaceId])

  // Loading states
  const isTypeaheadLoading = !!(enabled && (
    typeaheadResult === undefined || typeaheadResult.fetching
  ))
  const isTypeaheadError = !!typeaheadResult?.error
  const isOfferListLoading = enabled && !!predictionTypeaheadResult && predictionOfferList.fetching && !predictionOfferList.error
  const isLoading = (isTypeaheadLoading || isOfferListLoading) && !isTypeaheadError

  return {
    isTypeaheadLoading,
    isOfferListLoading,
    isLoading,
  }
}

export default useWrappedPredictionData
